import { useMemo, useState } from 'react';

import { TEXT_EDITOR_TYPE } from 'src/components/TextDetailsPanel';
import {
  useScoringMethodName,
  usePrepareDetectedBrandTerms,
  useEditorMarks,
  useTrackingEvent,
  useGetIntegrationsLastExtractionForDocument,
} from 'src/services';
import {
  useCurrentCustomer,
  useCustomFeatureLimitsData,
  useIsProjectReadOnly,
  useUiActions,
} from 'src/store';
import { getToolBriefType, isToolSupportBenchmark } from 'src/data/generationTool';
import { getChannelFromAssetType } from 'src/data/benchmark';
import * as events from 'src/lib/events';
import { getCustomerPlan, shouldUpgradeToBusiness } from 'src/data/subscription';
import { useEffectOnce } from 'src/hooks';
import * as ls from 'src/lib/localData';
import { FEATURE_LIMITS } from 'src/data/featureLimits';

const useTextDetailsPanelCounter = () => {
  const counter = ls.insightsPanelCounter.read();
  const increasedCounter = parseInt(counter) + 1;
  useEffectOnce(() => {
    ls.insightsPanelCounter.write(increasedCounter < 5 ? increasedCounter : undefined);
  });

  return increasedCounter;
};

const scoreIsNotAppealing = (score) => score < 60;
export const usePrepareTextDetailsPanelProps = ({
  editorContent,
  textEditorType = TEXT_EDITOR_TYPE.richSlate,
  onEditorContentChange,
  onEditorReferenceChange,
  localEditorRef,
  onCancelChanges,
  onSaveChanges,
  saveChangesEventData,
  blockedReason,
  isScoreLocked,
  readOnly,
  onScoringMethodClick,
  onApplyDetectedBrandTerm,
  onEditBrandTerm,
  customerHasAccessToPerformance,
  onBusinessBookDemoClick,
  userAllowedToBookDemo,
  brandTerms,
  assetType,
  scoringMethod,
  generationTool,
  updating,
  applyingChanges,
  onLockedUpgradeClick,
  onUndoClick,
  undoDisabled,
  detectedBrandTermsData,
  sensitivityData,
  scoreData,
  histogramsData,
  annotationsData,
  campaignKeywords,
  forceDisabled,
  scoringMethodName,
  secondaryScoringMethodName,
  displayScoringMethod = true,
  textForReadabilityScore,
  markedKeywordsMap,
  applicationMode,
  onEditorCopy,
  editorReadOnly,
  targetKeywordsData,
  relatedKeywordsData,
  onToggleMarkedKeyword,
  onSeoTrackerExpand,
  onBrandRulesExpand,
  plagiarismChecksUsed,
  plagiarismChecksLimit,
  hasPerformedPlagiarismCheck,
  potentialCopiedSentences,
  lastPlagiarismCheckTime,
  loadingPlagiarismReport,
  onCheckPlagiarism,
  textForPlagiarism,
  isOpen,
  plagiarismOnOpenCallback,
  targetAudienceProps,
  insightsBoxBorder,
  onTextCopy,
  onBenchmarkApplyClick,
  benchmarkConfigProps,
  trackingEventAttributes,
  resetCounter,
}) => {
  const currentCustomer = useCurrentCustomer();
  const uiActions = useUiActions();
  const editorMarks = useEditorMarks(detectedBrandTermsData);
  const preparedBrandTerms = usePrepareDetectedBrandTerms(detectedBrandTermsData);
  const isProjectReadOnly = useIsProjectReadOnly();
  const noneAppeal = scoreIsNotAppealing(scoreData?.score);
  const briefType = getToolBriefType(generationTool);
  const disabled = forceDisabled || isProjectReadOnly || updating || applyingChanges;
  const deducedScoringMethodName = useScoringMethodName(briefType, scoringMethod);
  const trackingEvent = useTrackingEvent();
  useGetIntegrationsLastExtractionForDocument();
  const resourcesRowsPerCustomerFeatureLimitsData = useCustomFeatureLimitsData(
    FEATURE_LIMITS.resourcesRowsPerCustomer
  );
  const [benchmarkPopupOpen, setBenchmarkPopupOpen] = useState();

  const textDetailsPanelCounter = useTextDetailsPanelCounter();

  const copyInputProps = useMemo(() => {
    const handleCopyInputSaveChanges = () => {
      onSaveChanges?.();

      saveChangesEventData?.name &&
        trackingEvent(saveChangesEventData.name, {
          assetType,
          generationTool,
          ...trackingEventAttributes,
          ...saveChangesEventData.attributes,
        });
    };

    return {
      assetType,
      generationTool,
      editorContent,
      onEditorContentChange,
      disabled,
      textEditorType,
      requireChangesConfirmation: true,
      editorRef: localEditorRef,
      onCancelChanges,
      onSaveChanges: handleCopyInputSaveChanges,
      editorMarks,
      applicationMode,
      onEditorCopy,
      readOnly: editorReadOnly,
      onEditorReferenceChange,
      onTextCopy,
      resetCounter,
    };
  }, [
    assetType,
    generationTool,
    editorContent,
    onEditorContentChange,
    disabled,
    textEditorType,
    localEditorRef,
    onCancelChanges,
    editorMarks,
    applicationMode,
    onEditorCopy,
    editorReadOnly,
    onEditorReferenceChange,
    onTextCopy,
    saveChangesEventData,
    trackingEvent,
    onSaveChanges,
    trackingEventAttributes,
    resetCounter,
  ]);

  const scoreDetailsProps = useMemo(
    () => ({
      briefType,
      score: scoreData?.score,
      basedOnValue: scoreData?.basedOn,
      loading: scoreData?.loading,
      blocked: !!blockedReason,
      locked: isScoreLocked,
      readOnly,
      displayScoringMethod,
      scoringMethodName: scoringMethodName || deducedScoringMethodName,
      onScoringMethodClick,
      campaignKeywords,
      noneAppeal,
    }),
    [
      blockedReason,
      briefType,
      campaignKeywords,
      deducedScoringMethodName,
      displayScoringMethod,
      isScoreLocked,
      noneAppeal,
      onScoringMethodClick,
      readOnly,
      scoreData?.basedOn,
      scoreData?.loading,
      scoreData?.score,
      scoringMethodName,
    ]
  );

  const scoreAdditionalFactorsProps = useMemo(
    () => ({
      campaignKeywords,
      loading: annotationsData?.loading,
      headlineType: secondaryScoringMethodName,
    }),
    [annotationsData?.loading, campaignKeywords, secondaryScoringMethodName]
  );

  const histogramAgeScoreProps = useMemo(
    () => ({
      loading: histogramsData?.loading,
      score: scoreData?.score,
      data: histogramsData?.age,
    }),
    [histogramsData?.age, histogramsData?.loading, scoreData?.score]
  );

  const histogramGenderScoreProps = useMemo(
    () => ({
      loading: histogramsData?.loading,
      data: histogramsData?.gender,
    }),
    [histogramsData?.gender, histogramsData?.loading]
  );

  const talkingPointsProps = useMemo(
    () => ({
      annotations: annotationsData?.annotations,
      loading: annotationsData?.loading,
    }),
    [annotationsData?.annotations, annotationsData?.loading]
  );

  const brandTermsProps = useMemo(
    () => ({
      terms: preparedBrandTerms?.items,
      onApplyDetectedBrandTerm,
      onEditBrandTerm,
      loading: preparedBrandTerms?.loading,
      disabled,
      customerHasAccessToPerformance,
      onBusinessBookDemoClick,
      userAllowedToBookDemo,
      hasBrandTerms: brandTerms?.length > 0,
      onExpand: onBrandRulesExpand,
      insightsBoxBorder,
    }),
    [
      brandTerms?.length,
      customerHasAccessToPerformance,
      disabled,
      onApplyDetectedBrandTerm,
      onBrandRulesExpand,
      onBusinessBookDemoClick,
      onEditBrandTerm,
      preparedBrandTerms?.items,
      preparedBrandTerms?.loading,
      userAllowedToBookDemo,
      insightsBoxBorder,
    ]
  );

  const channelPolicyProps = useMemo(
    () => ({
      assetType,
      sensitivity: sensitivityData?.sensitivity,
      loading: sensitivityData?.loading,
      horizontalLayout: true,
      policyTitle: true,
    }),
    [assetType, sensitivityData?.loading, sensitivityData?.sensitivity]
  );

  const blockedProps = useMemo(() => {
    if (!blockedReason) {
      return null;
    }

    return {
      blockedReason,
      onUpgradeClick: onLockedUpgradeClick,
      onUndoClick,
      undoDisabled,
    };
  }, [blockedReason, onLockedUpgradeClick, onUndoClick, undoDisabled]);

  const readabilityScoreProps = useMemo(
    () => ({
      copyText: textForReadabilityScore,
      insightsBoxBorder,
    }),
    [textForReadabilityScore, insightsBoxBorder]
  );

  const seoTrackerProps = useMemo(
    () => ({
      targetKeywordsData,
      relatedKeywordsData,
      onToggleMarkedKeyword,
      markedKeywordsMap,
      onExpand: onSeoTrackerExpand,
      insightsBoxBorder,
    }),
    [
      targetKeywordsData,
      relatedKeywordsData,
      onToggleMarkedKeyword,
      markedKeywordsMap,
      onSeoTrackerExpand,
      insightsBoxBorder,
    ]
  );

  const plagiarismCheckerProps = useMemo(
    () => ({
      copyText: textForPlagiarism,
      plagiarismChecksUsed,
      plagiarismChecksLimit,
      loading: loadingPlagiarismReport,
      lastPlagiarismCheckTime,
      hasPerformedPlagiarismCheck,
      onCheckPlagiarism,
      potentialCopiedSentences,
      isOpen,
      plagiarismOnOpenCallback,
      insightsBoxBorder,
    }),
    [
      plagiarismChecksUsed,
      plagiarismChecksLimit,
      lastPlagiarismCheckTime,
      hasPerformedPlagiarismCheck,
      onCheckPlagiarism,
      potentialCopiedSentences,
      loadingPlagiarismReport,
      textForPlagiarism,
      isOpen,
      plagiarismOnOpenCallback,
      insightsBoxBorder,
    ]
  );

  const audienceChartProps = useMemo(
    () => ({
      ...targetAudienceProps,
      empty: !scoreData?.loading && !(scoreData?.score > 0),
      loading: targetAudienceProps?.loading || scoreData?.loading,
    }),
    [targetAudienceProps, scoreData?.score, scoreData?.loading]
  );

  const benchmarkProps = useMemo(() => {
    if (!isToolSupportBenchmark(generationTool)) {
      return null;
    }
    const plan = getCustomerPlan(currentCustomer);
    const shouldUpgrade =
      shouldUpgradeToBusiness(plan) && (!textDetailsPanelCounter || textDetailsPanelCounter === 5);

    const channel = getChannelFromAssetType(assetType);
    const onBenchmarkConnectClick = () => {
      trackingEvent(events.DOCUMENT.variationBenchmarkConnectToChannelClicked, {
        channel,
        generationTool,
        ...trackingEventAttributes,
      });
    };
    const onBenchmarkUpgradeClick = () => {
      trackingEvent(events.DOCUMENT.variationBenchmarkUpgradeClicked, {
        channel,
        generationTool,
        ...trackingEventAttributes,
      });
    };
    const onBenchmarkPopupOpenClick = () => {
      // Enable extension fullscreen because it's narrower than the popup
      uiActions.increaseLocalPopupVisibleCount();
      setBenchmarkPopupOpen(true);
      trackingEvent(events.DOCUMENT.benchmarkPopupOpen, {
        channel,
        generationTool,
        ...trackingEventAttributes,
      });
    };

    const onBenchmarkPopupClose = () => {
      setBenchmarkPopupOpen(false);
      // Disable extension fullscreen
      uiActions.decreaseLocalPopupVisibleCount();
    };

    return {
      onBenchmarkConnectClick,
      onBenchmarkUpgradeClick,
      onApplyClick: onBenchmarkApplyClick,
      channel,
      assetType,
      shouldUpgrade,
      featureLimitsData: resourcesRowsPerCustomerFeatureLimitsData,
      popupOpen: benchmarkPopupOpen,
      openPopup: onBenchmarkPopupOpenClick,
      closePopup: onBenchmarkPopupClose,
      ...benchmarkConfigProps,
    };
  }, [
    currentCustomer,
    onBenchmarkApplyClick,
    generationTool,
    assetType,
    benchmarkConfigProps,
    trackingEvent,
    trackingEventAttributes,
    textDetailsPanelCounter,
    resourcesRowsPerCustomerFeatureLimitsData,
    benchmarkPopupOpen,
    setBenchmarkPopupOpen,
    uiActions,
  ]);

  return useMemo(
    () => ({
      copyInputProps,
      scoreDetailsProps,
      histogramAgeScoreProps,
      histogramGenderScoreProps,
      talkingPointsProps,
      brandTermsProps,
      channelPolicyProps,
      blockedProps,
      scoreAdditionalFactorsProps,
      readabilityScoreProps,
      seoTrackerProps,
      plagiarismCheckerProps,
      audienceChartProps,
      benchmarkProps,
    }),
    [
      copyInputProps,
      scoreDetailsProps,
      histogramAgeScoreProps,
      histogramGenderScoreProps,
      talkingPointsProps,
      brandTermsProps,
      channelPolicyProps,
      blockedProps,
      scoreAdditionalFactorsProps,
      readabilityScoreProps,
      seoTrackerProps,
      plagiarismCheckerProps,
      audienceChartProps,
      benchmarkProps,
    ]
  );
};
